import React, { useState } from "react";
import Modal from "ui/Modal";
import { Team } from "util/constants";
import ActorSearch, { ActorSuggestion } from "components/Common/ActorSearch";
import api from "config/axiosConfig";
import { toast } from "sonner";
import EntityAvatar from "components/Entities/EntityAvatar";
import { X, Info } from "lucide-react";

interface Props {
    team: Team;
    onClose: () => void;
    excludeIds: number[];
    onInviteSuccess: () => void;
}

const TeamInviteModal: React.FC<Props> = ({ team, onClose, excludeIds, onInviteSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<ActorSuggestion[]>([]);
    const [error, setError] = useState<string>("");

    const handleSelect = (actor: ActorSuggestion) => {
        if (!selectedUsers.some(user => user.id === actor.id)) {
            setSelectedUsers(prev => [...prev, actor]);
        }
    };

    const handleRemoveUser = (userId: number) => {
        setSelectedUsers(prev => prev.filter(user => user.id !== userId));
    };

    const handleInviteAll = async () => {
        if (selectedUsers.length === 0) {
            setError("Please select at least one user to invite");
            return;
        }

        setLoading(true);
        setError("");
        let successCount = 0;
        let failCount = 0;

        try {
            await Promise.all(selectedUsers.map(async (user) => {
                try {
                    await api.post(`/api/team/${team.id}/invite`, {
                        memberId: user.id,
                        memberType: user.type
                    });
                    successCount++;
                } catch (error) {
                    console.error(`Error inviting user ${user.name}:`, error);
                    failCount++;
                }
            }));

            if (successCount > 0) {
                toast.success(
                    `Successfully invited ${successCount} user${successCount > 1 ? 's' : ''}`
                );
                onInviteSuccess();
                onClose();
            }

            if (failCount > 0) {
                setError(
                    `Failed to invite ${failCount} user${failCount > 1 ? 's' : ''}`
                );
            }
        } catch (error) {
            console.error("Error in batch invites:", error);
            setError("Failed to process invitations");
        } finally {
            setLoading(false);
        }
    };

    // Convert ActorSuggestion to User type for EntityAvatar
    const convertToUser = (actor: ActorSuggestion): any => ({
        id: actor.id,
        id_short: actor.id.toString(),
        username: actor.username || actor.name,
        login_name: actor.username || actor.name,
        display_name: actor.display_name || actor.name,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        about: {},
        properties_public: {
            ...actor.properties_public
        }
    });

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title="Invite members to partea"
        >
            <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto overflow-x-hidden space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        Search for users to invite to {team.teamname}
                    </p>

                    <form className="flex flex-col flex-grow space-y-4">
                        <div className="form-control mb-0">
                            <ActorSearch
                                actorTypes={["user"]}
                                excludeIds={[...excludeIds, ...selectedUsers.map(u => u.id)]}
                                onSelect={handleSelect}
                                placeholder="Search users..."
                                inputType="add"
                                autoFocus={true}
                            />
                        </div>

                        {selectedUsers.length > 0 && (
                            <div className="form-control space-y-2">
                                <div className="text-sm font-medium text-eoe-text dark:text-eoe-dark-text">
                                    Selected Users ({selectedUsers.length})
                                </div>
                                <div className="max-h-[300px] overflow-y-auto space-y-2 bg-eoe-bg dark:bg-eoe-dark-bg rounded-2xl p-2">
                                    {selectedUsers.map((user) => (
                                        <div 
                                            key={user.id}
                                            className="flex items-center justify-between p-2 rounded-md bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover"
                                        >
                                            <div className="flex items-center gap-2">
                                                <EntityAvatar
                                                    entity={convertToUser(user)}
                                                    entityType="user"
                                                    entityChip={true}
                                                    showLink={true}
                                                    showCloseChip={false}
                                                    isInputField={false}
                                                />
                                            </div>
                                            <button
                                                onClick={() => handleRemoveUser(user.id)}
                                                className="p-1 hover:bg-eoe-bg dark:hover:bg-eoe-dark-bg rounded-full"
                                            >
                                                <X className="size-4 text-eoe-text dark:text-eoe-dark-text" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                                <Info className="mr-1 size-4" />
                                <span>{error}</span>
                            </div>
                        )}
                    </form>
                </div>

                <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                    <button
                        onClick={onClose}
                        className="btn-secondary w-full sm:w-fit py-2 px-4"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleInviteAll}
                        className={`w-full sm:w-fit py-2 px-4 ${
                            loading || selectedUsers.length === 0 ? 'btn-primary-disabled' : 'btn-primary'
                        }`}
                        disabled={loading || selectedUsers.length === 0}
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <span className="animate-spin">⏳</span>
                                <span>Inviting...</span>
                            </span>
                        ) : (
                            <span>Invite {selectedUsers.length > 0 ? `(${selectedUsers.length})` : ""}</span>
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default TeamInviteModal; 